<script setup>
import HkHeader from './components/HkHeader.vue'
import HkSidebar from './components/HkSidebar.vue'
import DynamicDialog from 'primevue/dynamicdialog'
import { useToast } from 'primevue/usetoast'
import { useObjectsStore } from '@/store/objects'
import { useOperationsStore } from '@/store/operations'
import { useLogsStore } from '@/store/logs'
import { useStatsStore } from '@/store/stats'
import { ref, provide, onMounted, watch } from 'vue'
import { useAuth } from './composables/auth'
import { useDark } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useStorage } from '@vueuse/core'
import { useAnomaliesStore } from '@/store/anomalies'
import { useDialog } from 'primevue/usedialog'
import UpdateAppDialog from './components/UpdateAppDialog.vue'

useDark({ selector: 'html' })
const { isAuthenticated } = useAuth()
const objectStore = useObjectsStore()
const operationsStore = useOperationsStore()
const logsStore = useLogsStore()
const statsStore = useStatsStore()
const anomaliesStore = useAnomaliesStore()
const toast = useToast()
const dialog = useDialog()

const sidebarCollapsed = useStorage('sidebarCollapsed', window.innerWidth < 780)
const currentEnvironment = useStorage('currentEnvironment', 'default')
const router = useRouter()

watch(currentEnvironment, () => {
  deRegisterForEvents()
  location.href = '/ui/'
})

const openAnomaly = ref(true)
watch(anomaliesStore.status, (newValue) => { openAnomaly.value = newValue.warning }, { immediate: true })

const windowTooSmall = ref(window.innerWidth < 780)
window.addEventListener('resize', () => {
  const val = window.innerWidth < 780
  if (windowTooSmall.value !== val) {
    windowTooSmall.value = val
  }
})
provide('sidebarCollapsed', sidebarCollapsed)
provide('windowTooSmall', windowTooSmall)

const eventListener = ref(null)
let keepAliveTimeout
const registerForEvents = async () => {
  if (!eventListener.value) {
    const resetTimeout = () => {
      clearTimeout(keepAliveTimeout)
      keepAliveTimeout = setTimeout(() => {
        console.info('Reconnecting to event stream')
        try { events.close() } catch (err) {}
        eventListener.value = null
        registerForEvents()
      }, 20000)
    }

    const events = new EventSource('/hk/v1/configuration/sync/events?x-environment=' + currentEnvironment.value)
    eventListener.value = events
    events.onerror = () => {
      /* network error. could happen. waiting for reconnect */
      console.info('Lost connection to event stream. Waiting for reconnect')
      try { events.close() } catch (err) {}
      eventListener.value = null
    }
    events.onmessage = (event) => {
      resetTimeout()
      if (event.data === 'ping') return
      const data = JSON.parse(event.data)
      switch (data.type) {
        case 'sync-logs':
          data.value.forEach(element => {
            try {
              element.createddate += 'Z'
              objectStore.updateSyncStatus(element.object, element.status)
              logsStore.updateSyncLogs(element)
              statsStore.refreshSyncStatusesMap(element.object, element.status)
            } catch (err) {
              console.error(err)
            }
          })
          break
        case 'operations-queue':
          data.value.forEach(element => {
            try {
              element.createddate += 'Z'
              operationsStore.updateOperationsQueue(element)
            } catch (err) {
              console.error(err)
            }
          })
          break
        case 'start-anomaly':
          toast.add({ severity: 'error', summary: 'New Anomaly detected!', detail: 'New synchronization issue has been detected!', group: 'tr' })
          anomaliesStore.updateStatus(true)
          break
        case 'end-anomaly':
          toast.add({ severity: 'success', summary: 'Anomaly resolved!', detail: 'Synchronization issue has been resolved', group: 'tr' })
          anomaliesStore.updateStatus(false)
          break
        case 'api-consumption':
          statsStore.updateApiConsumption(data.value)
          break
        case 'sync-count':
          objectStore.updateDbRows(data.value)
          break
      }
    }
  }
}

const deRegisterForEvents = () => {
  clearTimeout(keepAliveTimeout)
  if (eventListener.value) {
    eventListener.value.close()
    eventListener.value = null
  }
}

/* function touchStart (ev) {
  if ([...(ev.touches || [])].some(x => {
    return (x.clientX < window.innerWidth * 0.1 && windowTooSmall.value)
  })) {
    ev.preventDefault()
  }
} */

// Safari defaults to passive: true for the touchstart event, so we need  to explicitly specify false
// See https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
// const options = { passive: false }

// window.addEventListener('touchstart', touchStart, options)

onMounted(async () => {
  if ('serviceWorker' in navigator) {
    const { registerSW } = await import('virtual:pwa-register')
    const updateSW = registerSW({
      immediate: false,
      onNeedRefresh () {
        deRegisterForEvents()
        openUpdateModal(updateSW)
      },
      onOfflineReady () {},
    })
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      deRegisterForEvents()
    } else if (document.visibilityState === 'visible' && isAuthenticated.value) {
      registerForEvents()
    }
  })
})

const openUpdateModal = (updateSW) => {
  dialog.open(UpdateAppDialog, {
    data: {
      updateSw: updateSW
    },
    props: {
      closable: false,
      showHeader: false,
      style: {
        width: '300px',
        height: '400px'
      },

      contentStyle: 'overflow-y: hidden; display: flex; flex-direction: column; flex-grow: 1',
      modal: true
    }
  })
}

watch(isAuthenticated, (val, oldVal) => {
  if (val !== oldVal && !val) {
    deRegisterForEvents()
    router.push({ name: 'Login' })
  } else {
    registerForEvents()
  }
}, { immediate: true })
</script>
<template>
  <ConfirmDialog group="simple-dialog" />
  <DynamicDialog />
  <Toast
    position="top-right"
    group="tr"
  />
  <div
    class="layout-container"
    :style="{transition: isAuthenticated ? '' : 'none'}"
    :class="{ expanded: sidebarCollapsed || !isAuthenticated || windowTooSmall }"
  >
    <HkSidebar v-if="isAuthenticated" />
    <div class="layout-container-inner">
      <header
        class="sticky top-0 z-10 dark:border-l-gray-600"
        :class="{'border-l': !sidebarCollapsed && isAuthenticated && !windowTooSmall, 'h-16 ': isAuthenticated}"
      >
        <HkHeader v-if="isAuthenticated" />
      </header>
      <main
        id="main-container"
        class="flex flex-row overflow-auto h-full md:gap-4 min-h-[calc(100vh-112px)] md:min-h-[calc(100vh-70px)] md:ml-4"
        :class="{'h-full': !isAuthenticated}"
      >
        <div class="flex h-full w-full flex-grow min-w-0 flex-col md:pr-4 md:pb-4">
          <div class="flex h-full flex-col">
            <router-view v-slot="{ Component, route }">
              <component
                :is="Component"
                :key="route.path"
              />
            </router-view>
          </div>
        </div>
      </main>
      <footer
        v-if="isAuthenticated && windowTooSmall"
        class="sticky bottom-0 h-14 flex-shrink-0 flex justify-around flex-row items-center gap-2 px-2 md:px-4 dark:bg-[#1f1f1f] bg-gray-50  border-t-[#e0e3e8] border-t dark:dark:border-t-gray-600 pt-1"
      >
        <router-link
          v-slot="{ navigate, isActive }"
          to="/"
          custom
        >
          <div
            class="flex flex-col items-center h-full cursor-pointer pt-2"
            @click="navigate"
          >
            <i
              :class="`pi pi-chart-line ${isActive ? 'text-green-600' : 'text-gray-400'}`"
              style="font-size: 1.5rem"
            />
            <p
              :class="{'text-green-600': isActive, 'text-gray-400': !isActive }"
              style="scale: 0.75"
            >
              Dashboard
            </p>
          </div>
        </router-link>
        <router-link
          v-slot="{ navigate, isActive }"
          to="/objects"
          custom
        >
          <div
            class="flex flex-col items-center h-full cursor-pointer pt-2"
            @click="navigate"
          >
            <i
              :class="`pi pi-objects-column ${isActive ? 'text-sky-600' : 'text-gray-400'}`"
              style="font-size: 1.5rem"
            />
            <p
              :class="{'text-sky-600': isActive, 'text-gray-400': !isActive }"
              style="scale: 0.75"
            >
              All Mappings
            </p>
          </div>
        </router-link>
        <router-link
          v-slot="{ navigate, isActive }"
          to="/anomalies"
          custom
        >
          <div
            class="flex flex-col items-center h-full cursor-pointer pt-2 relative"
            @click="navigate"
          >
            <div
              v-if="openAnomaly"
              class="bg-red-500 flashit absolute"
              style="border-radius: 50%; top: 0px; right: 14px; padding: 0.45rem"
            />
            <i
              :class="`pi pi-exclamation-triangle  ${isActive ? 'text-red-700' : 'text-gray-400'}` "
              style="font-size: 1.5rem;"
            />

            <p
              :class="{'text-red-700': isActive, 'text-gray-400': !isActive }"
              style="scale: 0.75"
            >
              Anomalies
            </p>
          </div>
        </router-link>
      </footer>
    </div>
  </div>
</template>

<style lang="postcss">
.layout-container {
  @apply flex;
  @apply h-full;
  @apply ml-72;
  transition: margin-left 0.2s;
}
.p-togglebutton {
  @apply w-full;
}

.layout-container.expanded {
  @apply ml-0;
}

.layout-container-inner {
  @apply w-full;
  @apply flex;
  @apply flex-col;
  @apply md:gap-4;
}

.flashit{
  animation: flash linear 2s infinite;
}

@keyframes flash{
  0% {opacity: 1;}
  30% {opacity: .1;}
  100% {opacity: 1;}

}
</style>
